import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip as UITooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertTriangle, ChevronDown, ChevronRight, Info } from "lucide-react";
import { filterStatementsToLenders } from "@/lib/floorplanStats";

export function AgedInventorySection() {
  const { t } = useTranslation(["inventory", "common"]);
  const [open, setOpen] = useState(true);
  const { data: saved, isFetching: opAnalyticsFetching } = useQuery<{ input?: any; output?: any; sourceTaxYear?: number | null } | null>({ queryKey: ["/api/op-analytics/latest"], staleTime: 0 });
  const { data: savedTaxReturns = [], isFetching: savedReturnsFetching } = useQuery<any[]>({ queryKey: ["/api/saved-tax-returns"] });
  const { data: statements = [] } = useQuery<any[]>({ queryKey: ["/api/parsed-statements"] });
  const { data: lenders = [] } = useQuery<any[]>({ queryKey: ["/api/lenders"] });
  const { data: profile } = useQuery<any>({ queryKey: ["/api/dealer-profile"] });
  const { data: inventoryData } = useQuery<any>({
    queryKey: ["/api/floorplan-inventory"],
    queryFn: async () => (await apiRequest("POST", "/api/floorplan-inventory")).json(),
  });

  const linkedStatements = useMemo(
    () => filterStatementsToLenders(statements, lenders),
    [statements, lenders]
  );

  const output = saved?.output;
  const avgTurnDays: number = output?.inputsUsed?.avgTurnDays ?? 45;

  const LS_KEY = "aged-inventory-turn-target";

  const [turnTargetInput, setTurnTargetInput] = useState<string>("45");
  const [turnTargetInitialized, setTurnTargetInitialized] = useState(false);
  const [hasLocalOverride, setHasLocalOverride] = useState(false);

  useEffect(() => {
    if (!turnTargetInitialized && saved !== undefined) {
      const stored = localStorage.getItem(LS_KEY);
      if (stored !== null && !isNaN(parseInt(stored, 10)) && parseInt(stored, 10) >= 1) {
        setTurnTargetInput(stored);
        setHasLocalOverride(true);
      } else {
        setTurnTargetInput(String(avgTurnDays));
      }
      setTurnTargetInitialized(true);
    }
  }, [saved, turnTargetInitialized, avgTurnDays]);

  const persistTurnTarget = (value: string) => {
    const parsed = parseInt(value, 10);
    if (parsed >= 1) {
      localStorage.setItem(LS_KEY, String(parsed));
      setHasLocalOverride(true);
    }
  };

  const resetTurnTarget = () => {
    localStorage.removeItem(LS_KEY);
    setHasLocalOverride(false);
    setTurnTargetInput(String(avgTurnDays));
  };

  const effectiveTurnDays: number = Math.max(1, parseInt(turnTargetInput, 10) || avgTurnDays);

  // Average inventory units: prefer the value entered on Operational Analysis,
  // then the dealer-profile "avg units on lot". If neither exists, the dealer
  // can enter it inline below and it saves to their profile.
  const savedAvgUnits: number = saved?.input?.avgInventoryUnits ?? 0;
  const profileAvgUnits: number = profile?.avgUnitsOnLot != null ? parseFloat(String(profile.avgUnitsOnLot)) || 0 : 0;
  const liveAvgInventoryUnits: number = savedAvgUnits > 0 ? savedAvgUnits : profileAvgUnits;

  const [unitsInput, setUnitsInput] = useState("");
  const saveUnitsMutation = useMutation({
    mutationFn: (units: number) => apiRequest("POST", "/api/dealer-profile", { avgUnitsOnLot: units }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/dealer-profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/inventory/enriched"] });
      queryClient.invalidateQueries({ queryKey: ["/api/floorplan-inventory"] });
      queryClient.invalidateQueries({ queryKey: ["/api/op-analytics/latest"] });
    },
  });
  const submitUnits = () => {
    const parsed = parseInt(unitsInput, 10);
    if (parsed >= 1) saveUnitsMutation.mutate(parsed);
  };
  const liveTotalOperatingExpenses: number =
    (saved?.input?.annualTotalOperatingExpenses ?? 0) > 0
      ? (saved?.input?.annualTotalOperatingExpenses ?? 0)
      : (saved?.input?.annualTotalExpenses ?? 0);
  const livePerUnitDaily = liveAvgInventoryUnits > 0 && liveTotalOperatingExpenses > 0
    ? liveTotalOperatingExpenses / (liveAvgInventoryUnits * 365)
    : 0;
  const perUnitDaily: number = livePerUnitDaily > 0 ? livePerUnitDaily : (output?.costToCarry?.carryingCostPerCarPerDay ?? 0);

  const profileMonthlyRevenue: number | null = profile?.avgMonthlyRevenue != null ? parseFloat(String(profile.avgMonthlyRevenue)) : null;
  const profileSalesPerMonth: number | null = profile?.avgSalesPerMonth != null ? parseFloat(String(profile.avgSalesPerMonth)) : null;
  const profileVehicleValue: number | null = profile?.avgVehicleValue != null ? parseFloat(String(profile.avgVehicleValue)) : null;

  const extractEntryYear = (entry: any, snap: any): number => {
    if (snap?.taxYear) {
      const y = parseInt(snap.taxYear, 10);
      if (Number.isFinite(y) && y > 0) return y;
    }
    const nameMatch = typeof entry.name === "string" ? entry.name.match(/^(\d{4})\s*[–\-]/) : null;
    if (nameMatch) return parseInt(nameMatch[1], 10);
    const fileMatch = typeof entry.fileName === "string" ? entry.fileName.match(/\b(20\d{2})\b/) : null;
    if (fileMatch) return parseInt(fileMatch[1], 10);
    return 0;
  };

  const parsedTaxReturns: Array<{ entry: any; snap: any; year: number }> = useMemo(() => {
    return savedTaxReturns.map((entry: any) => {
      let snap: any = null;
      try { snap = JSON.parse(entry.snapshotJson); } catch { /* ignore */ }
      return { entry, snap, year: extractEntryYear(entry, snap) };
    });
  }, [savedTaxReturns]);

  const mostRecentTaxReturn: { entry: any; snap: any; year: number } | null = useMemo(() => {
    if (!parsedTaxReturns.length) return null;
    return parsedTaxReturns.reduce((best, r) => r.year > best.year ? r : best, parsedTaxReturns[0]);
  }, [parsedTaxReturns]);

  const sourceTaxYear: number | null = (mostRecentTaxReturn?.year ?? 0) > 0 ? mostRecentTaxReturn!.year : null;

  const taxReturnTotalRevenue: number | null = mostRecentTaxReturn?.snap?.revenue != null
    ? Number(mostRecentTaxReturn.snap.revenue)
    : null;
  const taxReturnUnitsSold: number | null = mostRecentTaxReturn?.snap?.annualUnitsSold != null
    ? Number(mostRecentTaxReturn.snap.annualUnitsSold)
    : null;
  const usingTaxReturnRevenue: boolean =
    taxReturnTotalRevenue != null && taxReturnUnitsSold != null && taxReturnUnitsSold > 0;

  const revenuePerUnit: number = usingTaxReturnRevenue
    ? taxReturnTotalRevenue! / taxReturnUnitsSold!
    : profileMonthlyRevenue != null && profileSalesPerMonth != null && profileSalesPerMonth > 0
      ? profileMonthlyRevenue / profileSalesPerMonth
      : profileVehicleValue ?? 0;

  const fmt$ = (v: number | null | undefined, d = 0) =>
    v == null || isNaN(v as number) ? "—" : `$${(v as number).toLocaleString("en-US", { maximumFractionDigits: d })}`;

  const revenueSource: string | null = usingTaxReturnRevenue && sourceTaxYear
    ? `tax-return-${sourceTaxYear}`
    : profileMonthlyRevenue != null && profileSalesPerMonth != null && profileSalesPerMonth > 0
      ? "profile"
      : profileVehicleValue != null
        ? "vehicle-value"
        : null;

  const taxReturnRevenueMissing: boolean =
    mostRecentTaxReturn != null &&
    taxReturnTotalRevenue != null &&
    (taxReturnUnitsSold == null || taxReturnUnitsSold <= 0);

  const revenueSourceLabel: string | null = revenueSource === null || revenuePerUnit === 0
    ? null
    : revenueSource.startsWith("tax-return-")
      ? t("inventory:aged.basedOnTaxReturn", { amount: fmt$(revenuePerUnit), year: revenueSource.replace("tax-return-", "") })
      : revenueSource === "profile"
        ? t("inventory:aged.basedOnProfile", { amount: fmt$(revenuePerUnit) })
        : t("inventory:aged.basedOnVehicleValue", { amount: fmt$(revenuePerUnit) });

  const { soldVins, soldStocks } = useMemo(() => {
    const items: any[] = Array.isArray(inventoryData?.items) ? inventoryData.items : [];
    const vins = new Set<string>();
    const stocks = new Set<string>();
    for (const item of items) {
      if (item.status !== "sold") continue;
      if (item.vin) vins.add(item.vin.trim().toUpperCase());
      if (item.stockNumber) stocks.add(item.stockNumber.trim().toUpperCase());
    }
    return { soldVins: vins, soldStocks: stocks };
  }, [inventoryData]);

  const { autoAgedUnits } = useMemo(() => {
    const units: Array<{ vin?: string; stock?: string; daysOutstanding: number; principal?: number }> = [];
    const seenKeys = new Set<string>();
    for (const s of linkedStatements) {
      try {
        const recvs = JSON.parse(s.receivablesJson || "[]");
        for (const r of recvs) {
          const days = r.daysOutstanding ?? r.daysFloored ?? 0;
          if (days <= 0) continue;
          const vinUpper = r.vin ? r.vin.trim().toUpperCase() : null;
          const stockUpper = r.stock ? r.stock.trim().toUpperCase() : null;
          if (vinUpper && soldVins.has(vinUpper)) continue;
          if (stockUpper && soldStocks.has(stockUpper)) continue;
          const key = vinUpper ?? stockUpper;
          if (key) { if (seenKeys.has(key)) continue; seenKeys.add(key); }
          units.push({ vin: r.vin, stock: r.stock, daysOutstanding: days, principal: r.principal });
        }
      } catch {}
    }
    return { autoAgedUnits: units.filter(u => u.daysOutstanding > effectiveTurnDays) };
  }, [linkedStatements, effectiveTurnDays, soldVins, soldStocks]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            {t("inventory:aged.title")}
          </CardTitle>
          {sourceTaxYear && !opAnalyticsFetching && !savedReturnsFetching && (
            <span className="text-xs text-muted-foreground shrink-0" data-testid="label-aged-inventory-tax-year">{t("inventory:aged.fromTaxYear", { year: sourceTaxYear })}</span>
          )}
        </div>
        <p className="text-sm text-muted-foreground flex items-center flex-wrap gap-x-1">
          {t("inventory:aged.unitsPastYour")}{" "}
          <input
            type="number"
            min={1}
            max={999}
            value={turnTargetInput}
            onChange={e => setTurnTargetInput(e.target.value)}
            onBlur={() => {
              const parsed = parseInt(turnTargetInput, 10);
              if (!parsed || parsed < 1) {
                const stored = localStorage.getItem(LS_KEY);
                if (stored !== null && parseInt(stored, 10) >= 1) {
                  setTurnTargetInput(stored);
                } else {
                  setTurnTargetInput(String(avgTurnDays));
                }
              } else {
                persistTurnTarget(String(parsed));
              }
            }}
            className="inline-block w-14 text-center text-sm font-semibold text-foreground bg-muted/60 border border-border/60 rounded px-1 py-0 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/60 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            data-testid="input-turn-day-target"
          />
          {hasLocalOverride && (
            <button
              type="button"
              onClick={resetTurnTarget}
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
              data-testid="button-reset-turn-target"
              title={t("inventory:aged.resetTitle")}
            >
              {t("inventory:aged.reset")}
            </button>
          )}
          {t("inventory:aged.turnTargetSuffix")}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {perUnitDaily === 0 ? (
          liveTotalOperatingExpenses > 0 && liveAvgInventoryUnits === 0 ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground flex gap-1.5 items-center">
                <Info className="w-4 h-4 shrink-0" />
                {t("inventory:aged.oneMoreNumber")}
              </p>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  value={unitsInput}
                  onChange={(e) => setUnitsInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") submitUnits(); }}
                  placeholder={t("inventory:aged.unitsPlaceholder")}
                  className="h-8 w-28 text-sm"
                  data-testid="input-avg-units-on-lot"
                />
                <Button
                  size="sm"
                  className="h-8 px-3 text-xs"
                  disabled={saveUnitsMutation.isPending || !(parseInt(unitsInput, 10) >= 1)}
                  onClick={submitUnits}
                  data-testid="button-save-avg-units"
                >
                  {saveUnitsMutation.isPending ? t("inventory:aged.saving") : t("inventory:aged.saveCalculate")}
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground flex gap-1.5 items-center">
              <Info className="w-4 h-4 shrink-0" />
              {t("inventory:aged.enterExpenses")}
            </p>
          )
        ) : autoAgedUnits.length === 0 ? (
          <p className="text-sm text-muted-foreground flex gap-1.5 items-center">
            <Info className="w-4 h-4 shrink-0" />
            {linkedStatements.length > 0
              ? t("inventory:aged.noAgedUnits", { days: effectiveTurnDays })
              : t("inventory:aged.uploadStatement")}
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl bg-muted/40 text-center">
                <p className="text-xs text-muted-foreground">{t("inventory:aged.agedUnits")}</p>
                <p className="text-2xl font-bold mt-1">{autoAgedUnits.length}</p>
              </div>
              <div className="p-4 rounded-xl bg-muted/40 text-center">
                <p className="text-xs text-muted-foreground">{t("inventory:aged.avgDaysOnFloor")}</p>
                <p className="text-2xl font-bold mt-1">
                  {Math.round(autoAgedUnits.reduce((s, u) => s + u.daysOutstanding, 0) / autoAgedUnits.length)}d
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("inventory:aged.vsTarget", { days: effectiveTurnDays })}</p>
              </div>
              <div className="p-4 rounded-xl bg-destructive/8 border border-destructive/20 text-center">
                <div className="flex items-center justify-center gap-1">
                  <p className="text-xs text-muted-foreground">{t("inventory:aged.potentialLostRevenue")}</p>
                  <UITooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3 h-3 text-muted-foreground/60 hover:text-muted-foreground cursor-default shrink-0" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[260px] text-xs">
                      {t("inventory:aged.lostRevenueTooltip")}
                      {revenuePerUnit > 0 && revenueSourceLabel && (
                        <span className="block mt-1 text-muted-foreground">{revenueSourceLabel}.</span>
                      )}
                    </TooltipContent>
                  </UITooltip>
                </div>
                <p className="text-2xl font-bold mt-1 text-destructive" data-testid="value-potential-lost-revenue">
                  {revenuePerUnit > 0
                    ? fmt$(autoAgedUnits.reduce((s, u) => {
                        const f = Math.max(0, u.daysOutstanding / effectiveTurnDays - 1);
                        return s + f * revenuePerUnit;
                      }, 0))
                    : "—"}
                </p>
                {revenueSourceLabel ? (
                  <p className="text-xs text-muted-foreground mt-1 leading-tight" data-testid="label-revenue-source">
                    {revenueSourceLabel}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground mt-1">—</p>
                )}
              </div>
            </div>

            {taxReturnRevenueMissing && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-3 py-2.5 text-xs text-amber-800 dark:text-amber-300" data-testid="notice-tax-return-units-missing">
                <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>
                  {t("inventory:aged.taxReturnMissingPrefix", { year: sourceTaxYear ? `${sourceTaxYear} ` : "" })}
                  {profileMonthlyRevenue != null && profileSalesPerMonth != null && profileSalesPerMonth > 0
                    ? t("inventory:aged.sourceMonthlyRevenue")
                    : profileVehicleValue != null
                      ? t("inventory:aged.sourceVehicleValue")
                      : t("inventory:aged.sourceDefault")}
                  {t("inventory:aged.taxReturnMissingSuffix")}
                </span>
              </div>
            )}

            <button
              type="button"
              onClick={() => setOpen(o => !o)}
              className="flex items-center gap-2 text-sm font-semibold text-foreground/80 hover:text-foreground transition-colors w-full py-2 px-3 rounded-lg bg-muted/30 hover:bg-muted/50 border border-border/50"
              data-testid="button-aged-units-toggle"
            >
              {open ? <ChevronDown className="w-4 h-4 shrink-0 text-primary" /> : <ChevronRight className="w-4 h-4 shrink-0 text-primary" />}
              {open ? t("inventory:aged.hideUnitDetail") : t("inventory:aged.showUnitDetail")}
              <span className="ml-auto text-xs font-normal text-muted-foreground">{t("inventory:aged.unitCount", { count: autoAgedUnits.length })}</span>
            </button>

            {open && (
              <div className="rounded-lg border overflow-hidden overflow-x-auto">
                <table className="w-full text-xs min-w-[500px]">
                  <thead>
                    <tr className="bg-muted/40 border-b">
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">{t("inventory:aged.colVinStock")}</th>
                      <th className="text-right py-2 px-3 font-medium text-muted-foreground">{t("inventory:aged.colDaysOnFloor")}</th>
                      <th className="text-right py-2 px-3 font-medium text-muted-foreground">{t("inventory:aged.colDaysPastTarget")}</th>
                      <th className="text-right py-2 px-3 font-medium text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                          {t("inventory:aged.colPotentialLostRevenue")}
                          <UITooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-3 h-3 text-muted-foreground/60 hover:text-muted-foreground cursor-default shrink-0" />
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[240px] text-xs">
                              {t("inventory:aged.unitLostRevenueTooltip")}
                            </TooltipContent>
                          </UITooltip>
                        </span>
                      </th>
                      <th className="text-right py-2 px-3 font-medium text-muted-foreground">{t("inventory:aged.colTotalCostToCarry")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...autoAgedUnits]
                      .sort((a, b) => b.daysOutstanding - a.daysOutstanding)
                      .map((u, i) => {
                        const pastTurn = Math.max(0, u.daysOutstanding - effectiveTurnDays);
                        const extraSalesFactor = Math.max(0, u.daysOutstanding / effectiveTurnDays - 1);
                        const potentialRevenueLost = extraSalesFactor * revenuePerUnit;
                        const holdingCost = u.daysOutstanding * perUnitDaily;
                        return (
                          <tr key={i} className="border-b last:border-0 hover:bg-muted/20">
                            <td className="py-2 px-3 font-mono">
                              {u.vin ? <span>{u.vin.slice(-8)}</span> : u.stock ? <span>#{u.stock}</span> : <span className="text-muted-foreground">—</span>}
                              {u.stock && u.vin && <span className="text-muted-foreground ml-1">#{u.stock}</span>}
                            </td>
                            <td className="py-2 px-3 text-right font-semibold">
                              <span className={u.daysOutstanding > effectiveTurnDays * 1.5 ? "text-destructive" : ""}>{u.daysOutstanding}d</span>
                            </td>
                            <td className="py-2 px-3 text-right text-muted-foreground">+{pastTurn}d</td>
                            <td className="py-2 px-3 text-right text-destructive font-medium">
                              {potentialRevenueLost > 0 ? fmt$(potentialRevenueLost) : "—"}
                            </td>
                            <td className="py-2 px-3 text-right text-amber-600 dark:text-amber-400 font-medium">
                              {perUnitDaily > 0 ? fmt$(holdingCost) : "—"}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                <p className="text-xs text-muted-foreground px-3 py-2 border-t bg-muted/10 space-y-0.5">
                  {revenuePerUnit > 0 && (
                    <span className="block">
                      {t("inventory:aged.revenuePerUnit", { amount: fmt$(revenuePerUnit) })}
                      {usingTaxReturnRevenue
                        ? t("inventory:aged.revenueFromTaxReturn")
                        : profileMonthlyRevenue != null && profileSalesPerMonth != null && profileSalesPerMonth > 0
                          ? t("inventory:aged.revenueFromProfile")
                          : profileVehicleValue != null
                            ? t("inventory:aged.revenueFromVehicleValue")
                            : ""}
                    </span>
                  )}
                  {perUnitDaily > 0 && (
                    <span className="block">{t("inventory:aged.totalCostFormula", { rate: perUnitDaily.toFixed(2) })}</span>
                  )}
                </p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
